import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Search, Plus, Trash2, Home, Sparkles, ChevronDown, MapPin } from "lucide-react";
import api from "@/lib/api";

/**
 * ArvFinder — subject property + comps → adjusted ARV, feeds into parent calculator.
 *
 * Manual comp entry today. When a comp API key is added later, the "Find Comps"
 * button auto-fills the list. Claude explains which comps look strongest.
 */

const fmt = (n) => "$" + Math.round(n || 0).toLocaleString();
const num = (v) => {
  const n = parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export default function ArvFinder({ onArvChange }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState({ address: "", sqft: "" });
  const [comps, setComps] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);

  const subjectSqft = num(subject.sqft);
  const analyzed = useMemo(() =>
    comps.map((c) => {
      const pricePerSqft = c.sqft > 0 ? c.price / c.sqft : 0;
      const adjusted = subjectSqft > 0 ? pricePerSqft * subjectSqft : c.price;
      return { ...c, pricePerSqft, adjusted };
    }), [comps, subjectSqft]);

  const included = analyzed.filter((c) => c.included);
  const arv = included.length
    ? included.reduce((s, c) => s + c.adjusted, 0) / included.length
    : 0;

  const addComp = () => {
    const c = {
      id: Math.random().toString(36).slice(2, 10),
      address: "",
      price: 0,
      sqft: 0,
      priceStr: "",
      sqftStr: "",
      included: true,
    };
    setComps([...comps, c]);
  };

  const updateComp = (id, field, val) => {
    setComps((prev) => prev.map((c) => {
      if (c.id !== id) return c;
      if (field === "priceStr") return { ...c, priceStr: val, price: num(val) };
      if (field === "sqftStr") return { ...c, sqftStr: val, sqft: num(val) };
      return { ...c, [field]: val };
    }));
  };

  const toggle = (id) => setComps((prev) => prev.map((c) =>
    c.id === id ? { ...c, included: !c.included } : c));

  const remove = (id) => setComps((prev) => prev.filter((c) => c.id !== id));

  const applyArv = () => {
    if (arv <= 0) return toast.error("Add at least one comp with price + sqft");
    onArvChange(Math.round(arv).toString());
    toast.success(`ARV set to ${fmt(arv)}`);
  };

  const askAi = async () => {
    if (included.length === 0) return toast.error("Add some comps first");
    setBusy(true);
    setAiAnalysis(null);
    try {
      const { data } = await api.post("/calculator/ai-review", {
        calc_type: "comps",
        inputs: {
          "Subject Address": subject.address || "unspecified",
          "Subject Sqft": subjectSqft || "unknown",
          Comps: included.map((c) => ({
            address: c.address || "unnamed",
            sold_price: c.price,
            sqft: c.sqft,
            price_per_sqft: Math.round(c.pricePerSqft),
            adjusted_value_for_subject: Math.round(c.adjusted),
          })),
          "Current Average ARV": Math.round(arv),
        },
        max_offer: Math.round(arv),
      });
      setAiAnalysis(data.review);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI analysis failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-5 rounded-2xl overflow-hidden" style={{ background: "var(--surface2)", border: "1px solid var(--border)" }} data-testid="arv-finder">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 transition hover:bg-white/5"
        data-testid="arv-finder-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--lime-glow)", color: "var(--lime)" }}>
            <Search size={15} />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold">ARV Finder</div>
            <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
              {arv > 0 ? `Current ARV: ${fmt(arv)} from ${included.length} comp${included.length === 1 ? "" : "s"}` : "Enter address + comps → auto-calculates ARV"}
            </div>
          </div>
        </div>
        <ChevronDown size={16} style={{ color: "var(--text-muted)", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
      </button>

      {open && (
        <div className="p-4 pt-0 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-2">
            <div className="relative">
              <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
              <input
                type="text"
                value={subject.address}
                onChange={(e) => setSubject({ ...subject, address: e.target.value })}
                placeholder="Subject property address"
                className="input-base pl-9 text-sm"
                data-testid="arv-subject-address"
              />
            </div>
            <input
              inputMode="numeric"
              value={subject.sqft}
              onChange={(e) => setSubject({ ...subject, sqft: e.target.value.replace(/[^\d]/g, "") })}
              placeholder="Subject sqft"
              className="input-base text-sm"
              data-testid="arv-subject-sqft"
            />
          </div>

          <div className="p-3 rounded-xl text-[11px]" style={{ background: "var(--surface)", color: "var(--text-muted)", border: "1px dashed var(--border)" }}>
            📍 Grab 3-5 recently sold comps from Zillow/Redfin/MLS (within 0.5 mi, similar bed/bath, sold in last 6 months). Paste them below and we&apos;ll adjust each by $/sqft to your subject.
          </div>

          {analyzed.map((c) => (
            <div key={c.id} className="p-3 rounded-xl space-y-2" style={{ background: c.included ? "var(--surface)" : "transparent", border: "1px solid var(--border)", opacity: c.included ? 1 : 0.55 }} data-testid={`arv-comp-${c.id}`}>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={c.included}
                  onChange={() => toggle(c.id)}
                  className="w-4 h-4 shrink-0"
                  style={{ accentColor: "var(--lime)" }}
                />
                <input
                  type="text"
                  value={c.address}
                  onChange={(e) => updateComp(c.id, "address", e.target.value)}
                  placeholder="Comp address"
                  className="input-base text-sm flex-1"
                />
                <button onClick={() => remove(c.id)} className="p-1.5 rounded hover:bg-white/5" style={{ color: "#f87171" }} title="Remove">
                  <Trash2 size={12} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input
                  inputMode="numeric"
                  value={c.priceStr}
                  onChange={(e) => updateComp(c.id, "priceStr", e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="Sold price"
                  className="input-base text-sm"
                />
                <input
                  inputMode="numeric"
                  value={c.sqftStr}
                  onChange={(e) => updateComp(c.id, "sqftStr", e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="Sqft"
                  className="input-base text-sm"
                />
                <div className="flex items-center px-3 rounded-xl text-xs font-mono" style={{ background: "var(--lime-glow)", color: "var(--lime)", border: "1px solid var(--border-lime)" }}>
                  {c.adjusted > 0 ? fmt(c.adjusted) : "—"}
                </div>
              </div>
              {c.pricePerSqft > 0 && (
                <div className="text-[10px] font-mono" style={{ color: "var(--text-dim)" }}>
                  ${Math.round(c.pricePerSqft)}/sqft{subjectSqft > 0 ? ` × ${subjectSqft} = ${fmt(c.adjusted)}` : ""}
                </div>
              )}
            </div>
          ))}

          <button
            onClick={addComp}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition"
            style={{ background: "transparent", border: "1px dashed var(--border-lime)", color: "var(--lime)" }}
            data-testid="arv-add-comp"
          >
            <Plus size={13} /> Add comp
          </button>

          <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: "var(--lime-glow)", border: "1px solid var(--border-lime)" }}>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: "var(--lime)" }}>Adjusted ARV</div>
              <div className="font-mono text-xl" style={{ color: "var(--lime)" }}>{fmt(arv)}</div>
            </div>
            <button
              onClick={applyArv}
              className="text-xs font-semibold px-4 py-2 rounded-xl"
              style={{ background: "var(--lime)", color: "#0c0c0c" }}
              data-testid="arv-apply"
            >
              Use this ARV
            </button>
          </div>

          <button
            onClick={askAi}
            disabled={busy || included.length === 0}
            className="btn-ghost w-full flex items-center justify-center gap-2 text-sm disabled:opacity-40"
            style={{ color: "var(--lime)" }}
            data-testid="arv-ai-analyze"
          >
            {busy ? <span className="spinner" /> : <Sparkles size={14} />}
            {busy ? "Analyzing comps..." : "Get AI comp analysis"}
          </button>

          {aiAnalysis && (
            <div className="ai-box" data-testid="arv-ai-analysis">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={13} style={{ color: "var(--lime)" }} />
                <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--lime)" }}>Comp analysis</span>
              </div>
              <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text)" }}>{aiAnalysis}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
